/* PromoteCampaignPicker — small caret button attached to a Promote button.
 * Opens a popover listing active sales campaigns. On selection, calls the
 * parent's onSelect(signalId, campaignId) which chains promote+enroll in a
 * single UX action. Signal-specific — different from the "add to campaign"
 * popover used on already promoted leads.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { ChevronDown, Loader2, Megaphone } from "lucide-react";

export function PromoteCampaignPicker({ signalId, onSelect, disabled, testidPrefix }) {
    const [open, setOpen] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);

    const openPicker = async (e) => {
        e.stopPropagation();
        setOpen(true);
        setLoading(true);
        try {
            const r = await api.get("/campaigns?type=sales");
            setCampaigns((r.data || []).filter((c) => c.status !== "completed"));
        } catch {
            toast.error(i18n.t("pipeline_sales.campaigns_load_failed"));
        } finally { setLoading(false); }
    };

    const pick = (campaignId) => {
        setOpen(false);
        onSelect?.(signalId, campaignId);
    };

    return (
        <div className="relative">
            <button onClick={openPicker} disabled={disabled} data-testid={`${testidPrefix}-open`}
                title="Promote + kies campagne"
                className="inline-flex items-center px-2 py-2 rounded-r-full bg-[var(--jr-brand)] text-black text-xs hover:bg-[var(--jr-brand-2)] disabled:opacity-60 border-l border-black/20">
                <ChevronDown size={12} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 w-72 rounded-xl border border-white/10 bg-[var(--jr-surface)] shadow-2xl overflow-hidden"
                        data-testid={`${testidPrefix}-popover`}>
                        <div className="p-3 border-b border-white/8">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Promote + enroll in</div>
                            <div className="text-xs text-white mt-0.5">{i18n.t("missions.pick_campaign")}</div>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                            {loading ? (
                                <div className="p-4 text-center"><Loader2 className="animate-spin inline" size={14} /></div>
                            ) : campaigns.length === 0 ? (
                                <div className="p-4 text-xs text-[var(--jr-text-3)] text-center">
                                    Nog geen actieve sales-campagnes.
                                    <Link to="/app/sales/campaigns" className="block mt-2 text-[var(--jr-brand)] hover:underline">
                                        Maak er één aan →
                                    </Link>
                                </div>
                            ) : (
                                <div className="divide-y divide-white/6">
                                    {campaigns.map((c) => (
                                        <button key={c.id} onClick={() => pick(c.id)}
                                            data-testid={`${testidPrefix}-select-${c.id}`}
                                            className="w-full text-left p-3 hover:bg-white/[0.03] flex items-center gap-2">
                                            <Megaphone size={12} className="text-[var(--jr-brand)] shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-white truncate">{c.name}</div>
                                                <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5">
                                                    {(c.steps || []).length} steps · {c.stats?.enrolled || 0} enrolled · <em>{c.status}</em>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="p-2 border-t border-white/8">
                            <Link to="/app/sales/campaigns"
                                className="block text-center text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)] hover:underline py-1">
                                + Beheer campagnes
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
